import React, { useEffect, useState, useRef } from "react";
import {
  Col,
  Input,
  Modal,
  Row,
  Spin,
  Typography,
  message,
  Popconfirm,
  Tooltip,
} from "antd";
import { CheckOutlined, CloseOutlined, DeleteOutlined, PlayCircleOutlined } from "@ant-design/icons";
import EditIcon from "../../../ui/EditIcon";
import NoDataPage from "../../../NoDataPages/NoDataPage";
import GalleryIcon from "../../../../assets/images/nurseIcons/gallery.svg";
import ReactPlayer from "react-player";
import dayjs from "dayjs";
import GridPdfViewer from "../../../PatientsIP/InvestigationsIp/InvestigationGallery/GridPdfViewer";
import PatientGalleryScroll from "../../../PatientsIP/PatientGallery/PatientGalleryScroll";
import {
  deletePatientUploadsApi,
  updateGalleryNameApi,
} from "../../../../services/patient.services";
import { sortGalleryFilesByName, validateGalleryFileName, getGalleryFileNameEditError } from "../../../../helpers/galleryUtils";
import "../../../PatientsIP/PatientGallery/PatientImageView.css";
import useIsMobile from "../../../../hooks/useIsMobile";

const TILE_W = 208;
// Enough room for media + 2-line name + date + Uploaded By/actions (avoids footer clip)
const TILE_H = 248;
const TILE_RADIUS = 7.31;
const TILE_BORDER = 0.73;
const MEDIA_H = 140;

const isImageFile = (item) =>
  item?.type?.includes("image") ||
  /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(item?.path || "") ||
  /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(item?.file_name || "");

const getYoutubeVideoId = (url) => {
  if (!url || typeof url !== "string") return null;
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  if (match) return match[1];
  const partial = url.match(/[?&]v=([A-Za-z0-9_-]{11})/);
  return partial ? partial[1] : null;
};

const normalizeVideoUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  if (
    trimmed.includes("youtube.com") ||
    trimmed.includes("youtu.be") ||
    trimmed.includes("watch?v=")
  ) {
    return trimmed.startsWith("www.")
      ? `https://${trimmed}`
      : `https://www.youtube.com/${trimmed.replace(/^\//, "")}`;
  }
  return trimmed;
};

const PatientImageViewOP = ({
  data,
  exercises,
  activeKey,
  refresh,
  isLoading = false,
  setLoading,
}) => {
  const isMobile = useIsMobile();
  const [selectedItem, setSelectedItem] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editName, setEditName] = useState("");
  const inputRef = useRef(null);
  const isRecycleBin = activeKey === "recycle_bin";

  const buildOpFileData = () =>
    data?.flatMap((item) => {
        const toPathList = (val) => {
          if (!val) return [];
          const list = Array.isArray(val) ? val : [val];
          return list.filter((p) => p && Object.keys(p).length > 0);
        };

        let rawPaths = toPathList(item.s3_paths);
        if (!rawPaths.length) rawPaths = toPathList(item.s3_path);
        if (!rawPaths.length) rawPaths = toPathList(item.file);

        const dataItems = rawPaths.map((action, pathIndex) => {
          const storedPath =
            item?.paths?.[pathIndex]?.path ?? action?.path ?? action?.key;
          const rawUrl = action?.url || (exercises ? item?.others_link : "") || "";
          const url = normalizeVideoUrl(rawUrl) || rawUrl;
          const youtubeId = getYoutubeVideoId(url);
          const looksLikeVideo =
            youtubeId ||
            action?.type?.includes("video") ||
            /\.(mp4|mov|avi|webm|m4v)(\?|$)/i.test(
              `${storedPath || ""} ${url} ${action?.file_name || ""}`
            );

          return {
            ...action,
            url,
            path: storedPath,
            type:
              action?.type ||
              (youtubeId
                ? "video/youtube"
                : looksLikeVideo
                  ? "video/mp4"
                  : action?.type),
            source_type: item?.source_type,
            others_link: item?.others_link,
            date: item?.created_at ?? item?.uploaded_at,
            upload_id: item?.upload_id ?? item?.id,
            uploaded_by_name: item?.uploaded_by_name,
            deleted_by: action?.deleted_by ?? item?.deleted_by,
            deleted_at: action?.deleted_at ?? item?.deleted_at,
            ...(exercises && {
              name: item.name,
              duration: item.duration,
              intensity: item.intensity,
              frequency: item.frequency,
              start_date: item.start_date,
            }),
          };
        });

        if (exercises && item.others_link) {
          const linkUrl = normalizeVideoUrl(item.others_link);
          const hasLinkEntry = dataItems.some(
            (entry) => getYoutubeVideoId(entry?.url) === getYoutubeVideoId(linkUrl)
          );
          if (!hasLinkEntry) {
            dataItems.push({
              url: linkUrl,
              type: "video/youtube",
              source_type: item?.source_type || "other_source",
              name: item.name,
              duration: item.duration,
              intensity: item.intensity,
              frequency: item.frequency,
              start_date: item.start_date,
              path: linkUrl,
              date: item.created_at,
            });
          }
        }

        return dataItems;
      }) ?? [];

  const buildRecycleBinFileData = () =>
    data?.flatMap((item) => {
      const s3Paths = item?.s3_paths ?? [];
      const pathMeta = item?.paths ?? [];

      return s3Paths.map((s3Path, pathIndex) => {
        const meta = pathMeta[pathIndex] ?? {};
        return {
          ...s3Path,
          path: meta?.path ?? s3Path?.path,
          date: item?.uploaded_at ?? item?.created_at,
          upload_id: item?.upload_id ?? item?.id,
          uploaded_by_name: item?.uploaded_by_name,
          deleted_by: meta?.deleted_by ?? item?.deleted_by,
          deleted_at: meta?.deleted_at ?? item?.deleted_at,
        };
      });
    }) ?? [];

  const fileData = sortGalleryFilesByName(
    isRecycleBin ? buildRecycleBinFileData() : buildOpFileData()
  );

  const handleSelectItem = (index) => setSelectedItem(index);

  // Reset inline edit state when switching tabs / data changes
  useEffect(() => {
    setEditingIndex(null);
    setEditName("");
  }, [activeKey, data, exercises]);

  const handleEditClick = (e, item, index) => {
    e.stopPropagation();
    setEditingIndex(index);
    setEditName(item?.file_name || item?.path?.split("/").pop() || "");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleEditConfirm = (item) => {
    const trimmedName = editName.trim();
    const validationError = validateGalleryFileName(trimmedName);
    if (validationError) {
      message.error(validationError);
      return;
    }

    onEditImage({ ...item, file_name: trimmedName });
    setEditingIndex(null);
    setEditName("");
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditName("");
  };

  const onEditImage = (item) => {
    const uploadId = item?.upload_id ?? item?.id;

    if (!uploadId) {
      message.error("Upload id not found");
      return;
    }

    setLoading?.(true);
    const payload = {
      paths: [
        {
          path: item?.path,
          file_name: item?.file_name,
        },
      ],
    };

    updateGalleryNameApi(uploadId, payload)
      .then((res) => {
        message.success(res?.data?.message || "File name updated successfully");
        refresh?.();
        setLoading?.(false);
      })
      .catch((err) => {
        message.error(err?.response?.data?.message || "Something went wrong");
        setLoading?.(false);
      });
  };

  const handleDelete = (item) => {
    const path = item?.path;
    const uploadId = item?.upload_id ?? item?.id;

    if (!uploadId) {
      message.error("Upload id not found");
      return;
    }
    if (!path) {
      message.error("File path not found");
      return;
    }

    const fileName = item?.file_name || path?.split("/")?.pop();
    const attemptDelete = (pathToSend) =>
      deletePatientUploadsApi({ upload_id: uploadId, path: pathToSend });

    attemptDelete(path)
      .then((res) => {
        message.success(res?.data?.message || "Deleted successfully");
        refresh?.();
      })
      .catch((err) => {
        const errMsg = err?.response?.data?.message || "";
        if (
          errMsg.includes("Path not found") &&
          fileName &&
          fileName !== path
        ) {
          attemptDelete(fileName)
            .then((res) => {
              message.success(res?.data?.message || "Deleted successfully");
              refresh?.();
            })
            .catch((retryErr) => {
              message.error(
                retryErr?.response?.data?.message || "Delete failed"
              );
            });
          return;
        }
        message.error(errMsg || "Delete failed");
      });
  };

  const renderMedia = (item) => {
    const videoUrl = normalizeVideoUrl(item?.url || item?.others_link || "");
    const youtubeId = getYoutubeVideoId(videoUrl);
    const isYoutube =
      !!youtubeId ||
      item?.type?.includes("youtube") ||
      item?.source_type === "other_source";
    const isVideo = item?.type?.includes("video") || isYoutube;

    if (isVideo) {
      if (youtubeId) {
        return (
          <ReactPlayer
            url={videoUrl}
            height={`${MEDIA_H}px`}
            width="100%"
            className="react-player"
            playsinline
            playIcon={
              <PlayCircleOutlined style={{ fontSize: 40, color: "#fff" }} />
            }
            light={`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`}
          />
        );
      }

      return (
        <ReactPlayer
          url={videoUrl}
          height={`${MEDIA_H}px`}
          width="100%"
          className="react-player"
          playsinline
          controls={!exercises}
        />
      );
    }

    if (isImageFile(item)) {
      return (
        <img
          className="gallery-tile-img"
          alt={item?.path?.replace("curengo/", "") || "Image"}
          src={item?.url}
        />
      );
    }

    return (
      <GridPdfViewer
        className="img-card"
        data={[item]}
        pageNumber={1}
        height={`${MEDIA_H}px`}
        width="100%"
        fill
      />
    );
  };

  return (
    <div>
      <div
        style={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
          overflowY: "visible",
          height: "auto",
          minHeight: isLoading ? 240 : 0,
          paddingBottom: "calc(32px + env(safe-area-inset-bottom, 0px))",
          backgroundColor: "#fff",
          position: "relative",
        }}
        className="gallery-card-container"
      >
        {isLoading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
              zIndex: 10,
            }}
          >
            <Spin tip="Loading patient gallery data, please wait ..." />
          </div>
        )}
        {fileData?.length > 0 ? (
          <div
            className="gallery-img-container"
            style={{
              overflow: "visible",
              display: "flex",
              width: "100%",
              backgroundColor: "#fff",
            }}
          >
            <Row style={{ width: "100%", display: "flex", flexWrap: "wrap", gap: 16 }}>
              {fileData?.map((item, index) => {
                const isEditing = editingIndex === index;

                return (
                <Col
                  key={index}
                  style={{
                    flex: isMobile ? "0 0 100%" : `0 0 ${TILE_W}px`,
                    maxWidth: isMobile ? "100%" : TILE_W,
                  }}
                >
                  {exercises && item.name && (
                    <Typography.Text
                      ellipsis
                      style={{
                        display: "block",
                        fontSize: 12,
                        fontWeight: 500,
                        color: "#222",
                        marginBottom: 6,
                        textAlign: isMobile ? "left" : "center",
                      }}
                    >
                      {item.name}
                    </Typography.Text>
                  )}
                  <div
                    className={[
                      "gallery-tile-card",
                      isEditing ? "gallery-tile-editing" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    style={{
                      width: isMobile ? "100%" : TILE_W,
                      height: "auto",
                      minHeight:
                        isRecycleBin
                          ? undefined
                          : isEditing
                            ? 268
                            : TILE_H,
                      borderRadius: TILE_RADIUS,
                      overflow: "visible",
                      border: `${TILE_BORDER}px solid #EAECF0`,
                      backgroundColor: "#fff",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <div
                      className="gallery-media-box"
                      style={{
                        cursor: "pointer",
                        height: MEDIA_H,
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                      onClick={() => handleSelectItem(index)}
                    >
                      {renderMedia(item)}
                    </div>
                    {!isRecycleBin ? (
                      <div
                        className="gallery-tile-info"
                        style={{
                          padding: "8px 8px 12px",
                          display: "flex",
                          flexDirection: "column",
                          flex: 1,
                          minHeight: isEditing ? 100 : 96,
                          boxSizing: "border-box",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isEditing ? (
                          <div style={{ marginBottom: 4 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <Input
                                ref={inputRef}
                                size="small"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onPressEnter={() => handleEditConfirm(item)}
                                status={
                                  getGalleryFileNameEditError(editName) ? "error" : undefined
                                }
                                style={{ flex: 1, fontSize: 13 }}
                              />
                              <CheckOutlined
                                onClick={() => handleEditConfirm(item)}
                                style={{
                                  color: "#52c41a",
                                  cursor: "pointer",
                                  fontSize: 14,
                                }}
                              />
                              <CloseOutlined
                                onClick={handleEditCancel}
                                style={{
                                  color: "#ff4d4f",
                                  cursor: "pointer",
                                  fontSize: 14,
                                }}
                              />
                            </div>
                            {getGalleryFileNameEditError(editName) && (
                              <Typography.Text
                                type="danger"
                                style={{
                                  display: "block",
                                  fontSize: 11,
                                  marginTop: 4,
                                  lineHeight: "14px",
                                }}
                              >
                                {getGalleryFileNameEditError(editName)}
                              </Typography.Text>
                            )}
                          </div>
                        ) : (
                          <Typography.Text
                            className="gallery-tile-filename"
                            style={{
                              display: "block",
                              fontSize: 12,
                              fontWeight: 500,
                              color: "#222",
                              marginBottom: 2,
                              lineHeight: 1.35,
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                              whiteSpace: "normal",
                            }}
                            title={
                              exercises
                                ? item?.name ||
                                  item?.file_name ||
                                  item?.path?.split("/").pop()
                                : item?.file_name || item?.path?.split("/").pop()
                            }
                          >
                            {exercises
                              ? item?.name ||
                                item?.file_name ||
                                item?.path?.split("/").pop()
                              : item?.file_name || item?.path?.split("/").pop()}
                          </Typography.Text>
                        )}

                        {/* Date */}
                        <Typography.Text style={{ fontSize: 11, color: "#888" }}>
                          {item?.date
                            ? dayjs(item.date).format("DD MMM, hh:mm A")
                            : "Unknown Date"}
                        </Typography.Text>

                        {/* Bottom row: Uploaded by + actions (keep Uploaded By visible while editing) */}
                        {editingIndex !== index && <div style={{ flex: 1 }} />}
                        {!exercises && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 10,
                              marginTop: isEditing ? 8 : 6,
                              flexShrink: 0,
                            }}
                          >
                            <Tooltip title={item?.uploaded_by_name || "-"}>
                              <Typography.Text
                                ellipsis
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  fontSize: 11,
                                  color: "#667085",
                                }}
                              >
                                Uploaded By: {item?.uploaded_by_name || "-"}
                              </Typography.Text>
                            </Tooltip>

                            {editingIndex !== index && (
                              <div style={{ display: "flex", gap: 10 }}>
                                <EditIcon
                                  onClick={(e) => handleEditClick(e, item, index)}
                                  style={{
                                    color: "#888",
                                    cursor: "pointer",
                                    fontSize: 13,
                                  }}
                                />
                                <Popconfirm
                                  title="Are you sure you want to delete this file?"
                                  okText="Yes"
                                  cancelText="No"
                                  onConfirm={() => handleDelete(item)}
                                >
                                  <DeleteOutlined
                                    style={{
                                      color: "#ff4d4f",
                                      cursor: "pointer",
                                      fontSize: 13,
                                    }}
                                  />
                                </Popconfirm>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div
                        style={{ padding: "8px 6px 10px" }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Typography.Text
                          style={{
                            display: "block",
                            fontSize: 12,
                            fontWeight: 600,
                            color: "#222",
                            marginBottom: 6,
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                            lineHeight: "16px",
                          }}
                        >
                          {item?.file_name ||
                            item?.path?.split("/")?.pop() ||
                            "Unnamed file"}
                        </Typography.Text>
                        <Typography.Text
                          style={{
                            display: "block",
                            fontSize: 11,
                            color: "#667085",
                            lineHeight: "15px",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                          }}
                        >
                          <span style={{ fontWeight: 600, color: "#344054" }}>
                            Deleted By:
                          </span>{" "}
                          {item?.deleted_by || "-"}
                        </Typography.Text>
                        <Typography.Text
                          style={{
                            display: "block",
                            fontSize: 11,
                            color: "#667085",
                            marginTop: 2,
                            lineHeight: "15px",
                          }}
                        >
                          <span style={{ fontWeight: 600, color: "#344054" }}>
                            Deleted At:
                          </span>{" "}
                          {item?.deleted_at
                            ? dayjs(item.deleted_at).format("DD MMM, hh:mm A")
                            : "Unknown Date"}
                        </Typography.Text>
                      </div>
                    )}
                  </div>
                </Col>
                );
              })}
            </Row>
          </div>
        ) : !isLoading ? (
          <Row
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100%",
              backgroundColor: "white",
              width: "100vw",
              maxWidth: "100%",
              overflowX: "hidden",
            }}
          >
            <Col span={24} style={{ textAlign: "center", padding: "20px" }}>
              <NoDataPage
                title="Gallery Empty"
                subtitle="No images to be shown at the moment!"
                image={GalleryIcon}
              />
            </Col>
          </Row>
        ) : null}
      </div>

      {typeof selectedItem === "number" && (
        <Modal
          open={typeof selectedItem === "number"}
          onCancel={() => setSelectedItem(null)}
          width="min(920px, 96vw)"
          footer={null}
          centered
          destroyOnClose
          maskClosable={false}
          wrapClassName={
            exercises
              ? "patient-gallery-lightbox patient-gallery-lightbox-exercises"
              : "patient-gallery-lightbox"
          }
          title={
            <Row>
              <Col span={24}>
                <Typography.Text strong>
                  {fileData[selectedItem]?.name ||
                    fileData[selectedItem]?.file_name ||
                    fileData[selectedItem]?.path?.split("/")?.pop() ||
                    "Gallery"}
                </Typography.Text>
              </Col>
            </Row>
          }
        >
          <PatientGalleryScroll
            exercises={exercises}
            selectedGroup={fileData}
            selectedItem={selectedItem}
          />
        </Modal>
      )}
    </div>
  );
};

export default PatientImageViewOP;
